// src/seo/PageSeo.jsx
//
// Componente único que injeta <title>, <meta description>, <link canonical>,
// Open Graph, Twitter Card e (opcionalmente) JSON-LD por rota pública.
//
// O HTML base em `public/index.html` já carrega o JSON-LD canônico (Organization /
// MedicalBusiness / WebSite / Service / FAQPage). Este componente complementa com
// metas dinâmicas por rota e BreadcrumbList; bots como Googlebot que executam JS
// leem o resultado final. Para crawlers que não executam JS, o build grava o HTML
// das páginas públicas principais com o mesmo head (`pageSeoHead`, usado em
// scripts/prerender-conteudo.js).

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { publicOriginForSeo } from '../lib/siteHosts';

const SITE_NAME = 'Consulta+';

/**
 * Valores do head de uma rota. Compartilhado com o pré-render do build.
 *
 * @param {object} props Mesmas props do PageSeo (title, path, image, breadcrumbs, jsonLd).
 * @param {string} [siteUrl] Origem do canonical e do og:url.
 */
export function pageSeoHead(
  { title, path = '/', image, breadcrumbs, jsonLd },
  siteUrl = publicOriginForSeo(),
) {
  const url = `${siteUrl}${path === '/' ? '/' : path.replace(/\/+$/, '')}`;
  const ogImage = image || `${siteUrl}/logo.png`;
  // Reconhece variantes do brand para não duplicar sufixo (ex.: já tem
  // "Consulta+", "Consulta Mais" ou "ConsultaMais" no título).
  const hasBrand = /Consulta\+?|Consulta\s*Mais/i.test(title || '');
  const fullTitle = hasBrand ? title : `${title} | ${SITE_NAME}`;

  const breadcrumbJson =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: b.name,
            ...(b.url ? { item: `${siteUrl}${b.url}` } : {}),
          })),
        }
      : null;

  const extraJsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return { url, ogImage, fullTitle, breadcrumbJson, extraJsonLdArray };
}

/**
 * @param {object} props
 * @param {string} props.title       Title da aba (recomendado <= 60 chars).
 * @param {string} props.description Meta description (recomendado 140-160 chars).
 * @param {string} props.path        Caminho relativo (ex.: "/diretor"). Vira canonical e og:url.
 * @param {string} [props.image]     URL absoluta de imagem OG. Padrão: logo.png.
 * @param {boolean} [props.noindex]  Se true, emite robots noindex,nofollow.
 * @param {Array<{name: string, url?: string}>} [props.breadcrumbs]
 *        Trilha para BreadcrumbList JSON-LD. Primeiro item normalmente é
 *        { name: 'Início', url: '/' }.
 * @param {object} [props.jsonLd]    JSON-LD adicional específico da rota
 *        (objeto único ou array). Será serializado em <script type="application/ld+json">.
 */
export default function PageSeo({
  title,
  description,
  path = '/',
  image,
  noindex = false,
  breadcrumbs,
  jsonLd,
}) {
  const { url, ogImage, fullTitle, breadcrumbJson, extraJsonLdArray } = pageSeoHead({
    title,
    path,
    image,
    breadcrumbs,
    jsonLd,
  });

  // No React 19 o Helmet acrescenta as tags ao head sem tirar as do
  // public/index.html (e do HTML pré-renderizado). Sem esta limpeza a página
  // fica com duas canonicals e duas descriptions.
  useEffect(() => {
    document.head.querySelectorAll('[data-seo-default]').forEach((el) => el.remove());
  }, []);

  return (
    <Helmet>
      <html lang="pt-BR" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {breadcrumbJson && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJson)}
        </script>
      )}
      {extraJsonLdArray.map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
